/**
 * Brain-dump segmentation.
 *
 * Splits raw, messy prose into individual task candidates without requiring
 * the user to format anything. Handles newlines, list bullets, sentence
 * boundaries, semicolons, and "and"-chained clauses that look like
 * independent actions.
 */

const ACTION_VERBS = new Set([
  'fix', 'finish', 'call', 'email', 'reply', 'respond', 'review', 'write',
  'draft', 'send', 'buy', 'get', 'pick', 'prep', 'prepare', 'plan', 'book',
  'schedule', 'clean', 'clear', 'organize', 'organise', 'pay', 'file',
  'submit', 'update', 'launch', 'ship', 'deploy', 'test', 'debug', 'design',
  'build', 'create', 'research', 'read', 'study', 'learn', 'exercise',
  'workout', 'meet', 'handle', 'sort', 'check', 'follow', 'renew', 'cancel',
  'order', 'return', 'sign', 'print', 'backup', 'migrate', 'refactor',
  'practice', 'practise', 'apply', 'invoice', 'budget', 'stop', 'quit',
  'scroll', 'watch', 'browse', 'grab', 'drop', 'wash', 'walk', 'train',
]);

const LEADING_FILLER =
  /^(i\s+(?:(?:also|really|just|still|honestly)\s+)*(need|have|want|got|gotta|should|must)\s+to\s+|i\s+(?:(?:also|really|just|still|honestly)\s+)*(need|have|want|gotta|should|must)\s+|need\s+to\s+|have\s+to\s+|gotta\s+|should\s+|must\s+|also\s+|then\s+|maybe\s+|don'?t\s+forget\s+to\s+|remember\s+to\s+|make\s+sure\s+(i|to)\s+)/i;

function startsWithActionVerb(clause: string): boolean {
  const cleaned = clause.trim().replace(LEADING_FILLER, '');
  const first = cleaned.split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '');
  return first !== undefined && ACTION_VERBS.has(first);
}

/** Strip filler prefixes and normalise whitespace/casing of a task string. */
export function normalizeTask(raw: string): string {
  let t = raw.trim();
  // Remove list markers and stray punctuation.
  t = t.replace(/^[-*•\d.)\s]+/, '').trim();
  // Iteratively strip filler ("I also need to ...").
  let prev = '';
  while (prev !== t) {
    prev = t;
    t = t.replace(LEADING_FILLER, '').trim();
  }
  t = t.replace(/[.\s]+$/, '').trim();
  if (t.length === 0) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Split a raw brain dump into individual task candidate strings. */
export function segment(rawInput: string): string[] {
  const primary = rawInput
    // Newlines are ALWAYS a boundary; sentence enders/semicolons only when
    // followed by space or end ("v2.0" stays whole). 2 Nov 2026: the
    // lookahead used to cover the newline too, so a typed or dictated list
    // ("call the bank\npick up groceries") stayed ONE task on-device.
    .split(/\r?\n|[;.!?]+(?=\s|$)/)
    .map((s) => s.trim())
    .filter(Boolean);

  const results: string[] = [];
  for (const chunk of primary) {
    // Split "and"/comma chains only when the following clause reads like a
    // fresh action ("fix the bug and review the contract" → two tasks, but
    // "peanut butter and jelly" stays intact). The separator is captured so
    // it can be restored verbatim when clauses are merged back together.
    const parts = chunk.split(/(,\s*(?:and\s+)?|\s+and\s+|\s+&\s+|\s+then\s+)/i);
    let buffer = '';
    for (let i = 0; i < parts.length; i += 2) {
      const part = parts[i];
      const sepBefore = i > 0 ? parts[i - 1] : '';
      if (!buffer) {
        buffer = part;
        continue;
      }
      if (startsWithActionVerb(part)) {
        results.push(buffer);
        buffer = part;
      } else {
        buffer += sepBefore + part;
      }
    }
    if (buffer) results.push(buffer);
  }

  const seen = new Set<string>();
  const tasks: string[] = [];
  for (const r of results) {
    const t = normalizeTask(r);
    if (t.length < 3) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    tasks.push(t);
  }
  return tasks;
}
